import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { b2cBoardKeys } from "@/application/query-keys/b2c-board.keys";
import {
  b2cBoardRepository,
  type B2cTaskPatch,
  type CreateB2cTaskInput,
  type IterationBranchPatch,
  type IterationPatch,
  type StagePatch,
  type WorkstreamPatch,
} from "@/infrastructure/repositories/b2c-board.supabase.repository";
import type {
  B2cIterationRow,
  B2cRepo,
  B2cTaskRow,
  IterationStatus,
} from "@/infrastructure/supabase/b2c-board.types";

const FRESH = { staleTime: 30 * 1000 };

// Every board write is logged by a database trigger (0145), so every mutation
// refreshes the activity feeds too.
function invalidateActivity(queryClient: ReturnType<typeof useQueryClient>) {
  return queryClient.invalidateQueries({ queryKey: b2cBoardKeys.activity() });
}

export function useB2cStages() {
  return useQuery({
    queryKey: b2cBoardKeys.stages(),
    queryFn: () => b2cBoardRepository.listStages(),
    ...FRESH,
  });
}

export function useB2cWorkstreams() {
  return useQuery({
    queryKey: b2cBoardKeys.workstreams(),
    queryFn: () => b2cBoardRepository.listWorkstreams(),
    ...FRESH,
  });
}

export function useB2cGates() {
  return useQuery({
    queryKey: b2cBoardKeys.gates(),
    queryFn: () => b2cBoardRepository.listGates(),
    ...FRESH,
  });
}

export function useB2cIterations() {
  return useQuery({
    queryKey: b2cBoardKeys.iterations(),
    queryFn: () => b2cBoardRepository.listIterations(),
    ...FRESH,
  });
}

export function useB2cIterationBranches() {
  return useQuery({
    queryKey: b2cBoardKeys.branches(),
    queryFn: () => b2cBoardRepository.listIterationBranches(),
    ...FRESH,
  });
}

// A branch merging can merge its iteration and close its tasks (database triggers,
// 0144), so a branch write refreshes all three.
function invalidateAfterBranchWrite(queryClient: ReturnType<typeof useQueryClient>) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: b2cBoardKeys.branches() }),
    queryClient.invalidateQueries({ queryKey: b2cBoardKeys.iterations() }),
    queryClient.invalidateQueries({ queryKey: b2cBoardKeys.tasks() }),
    invalidateActivity(queryClient),
  ]);
}

export function useUpsertB2cIterationBranch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      iterationId,
      repo,
      patch,
    }: {
      iterationId: string;
      repo: B2cRepo;
      patch: IterationBranchPatch & { branch: string };
    }) => b2cBoardRepository.upsertIterationBranch(iterationId, repo, patch),
    onSettled: () => invalidateAfterBranchWrite(queryClient),
  });
}

export function useUpdateB2cIterationBranch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: IterationBranchPatch }) =>
      b2cBoardRepository.updateIterationBranch(id, patch),
    onSettled: () => invalidateAfterBranchWrite(queryClient),
  });
}

export function useB2cTasks() {
  return useQuery({
    queryKey: b2cBoardKeys.tasks(),
    queryFn: () => b2cBoardRepository.listTasks(),
    ...FRESH,
  });
}

// Task edits patch the cached list first so a status change shows at once, and
// roll back if the write is rejected (e.g. RLS for a view-only user).
export function useUpdateB2cTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: B2cTaskPatch }) =>
      b2cBoardRepository.updateTask(id, patch),
    onMutate: async ({ id, patch }) => {
      await queryClient.cancelQueries({ queryKey: b2cBoardKeys.tasks() });
      const prev = queryClient.getQueryData<B2cTaskRow[]>(b2cBoardKeys.tasks());
      queryClient.setQueryData<B2cTaskRow[]>(b2cBoardKeys.tasks(), (old) =>
        old ? old.map((t) => (t.id === id ? { ...t, ...patch } : t)) : old
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(b2cBoardKeys.tasks(), ctx.prev);
    },
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: b2cBoardKeys.tasks() }),
        invalidateActivity(queryClient),
      ]),
  });
}

export function usePlanB2cTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      iterationId,
      sortOrder,
    }: {
      id: string;
      iterationId: string;
      sortOrder: number;
    }) => b2cBoardRepository.planTask(id, iterationId, sortOrder),
    onMutate: async ({ id, iterationId, sortOrder }) => {
      await queryClient.cancelQueries({ queryKey: b2cBoardKeys.tasks() });
      const prev = queryClient.getQueryData<B2cTaskRow[]>(b2cBoardKeys.tasks());
      queryClient.setQueryData<B2cTaskRow[]>(b2cBoardKeys.tasks(), (old) =>
        old
          ? old.map((t) =>
              t.id === id ? { ...t, iteration_id: iterationId, sort_order: sortOrder } : t
            )
          : old
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(b2cBoardKeys.tasks(), ctx.prev);
    },
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: b2cBoardKeys.tasks() }),
        invalidateActivity(queryClient),
      ]),
  });
}

export function useDeleteB2cTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => b2cBoardRepository.deleteTask(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: b2cBoardKeys.tasks() });
      const prev = queryClient.getQueryData<B2cTaskRow[]>(b2cBoardKeys.tasks());
      queryClient.setQueryData<B2cTaskRow[]>(b2cBoardKeys.tasks(), (old) =>
        old ? old.filter((t) => t.id !== id) : old
      );
      return { prev };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(b2cBoardKeys.tasks(), ctx.prev);
    },
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: b2cBoardKeys.tasks() }),
        invalidateActivity(queryClient),
      ]),
  });
}

export function useCreateB2cTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateB2cTaskInput) => b2cBoardRepository.createTask(input),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: b2cBoardKeys.tasks() }),
        invalidateActivity(queryClient),
      ]),
  });
}

export function useCreateB2cIteration() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      workstreamKey,
      number,
      status,
    }: {
      workstreamKey: string;
      number: number;
      status?: IterationStatus;
    }) => b2cBoardRepository.createIteration(workstreamKey, number, status),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: b2cBoardKeys.iterations() }),
        invalidateActivity(queryClient),
      ]),
  });
}

export function useUpdateB2cIteration() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: IterationPatch }) =>
      b2cBoardRepository.updateIteration(id, patch),
    onSuccess: (row) => {
      queryClient.setQueryData<B2cIterationRow[]>(b2cBoardKeys.iterations(), (old) =>
        old ? old.map((i) => (i.id === row.id ? row : i)) : old
      );
    },
    // Merging an iteration closes its in-review tasks (database trigger, 0144).
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: b2cBoardKeys.iterations() }),
        queryClient.invalidateQueries({ queryKey: b2cBoardKeys.tasks() }),
        invalidateActivity(queryClient),
      ]),
  });
}

export function useUpdateB2cWorkstream() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ key, patch }: { key: string; patch: WorkstreamPatch }) =>
      b2cBoardRepository.updateWorkstream(key, patch),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: b2cBoardKeys.workstreams() }),
        invalidateActivity(queryClient),
      ]),
  });
}

export function useUpdateB2cStage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ key, patch }: { key: string; patch: StagePatch }) =>
      b2cBoardRepository.updateStage(key, patch),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: b2cBoardKeys.stages() }),
        invalidateActivity(queryClient),
      ]),
  });
}

const ACTIVITY_PAGE = 50;

/** The whole board's activity, newest first; fetched only while the feed is open. */
export function useB2cActivity(options?: { enabled?: boolean }) {
  return useInfiniteQuery({
    queryKey: b2cBoardKeys.activity(),
    queryFn: ({ pageParam }) =>
      b2cBoardRepository.listActivity({ cursor: pageParam, limit: ACTIVITY_PAGE }),
    initialPageParam: null as number | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled: options?.enabled ?? true,
  });
}

/** One task's activity, newest first. */
export function useB2cTaskActivity(taskId: string | undefined) {
  return useInfiniteQuery({
    queryKey: b2cBoardKeys.taskActivity(taskId ?? ""),
    queryFn: ({ pageParam }) =>
      b2cBoardRepository.listActivity({ taskId, cursor: pageParam, limit: ACTIVITY_PAGE }),
    initialPageParam: null as number | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled: !!taskId,
  });
}
